import { Button, Loading } from '@brighterion/br-ui';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import useCreateSingleReportCSV from 'Hooks/query-hooks/reports/useCreateSingleReportCSV';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { ExportCSVErrorModal } from './ExportCSVErrorModal';

type Props = {
    extendedFormValues: ProcessedNewMerchantReportFormValues | undefined;
    setSaveBtnClicked: Dispatch<SetStateAction<boolean>>;
    reportType: ReportType;
};

const ExportCSV = ({ extendedFormValues, setSaveBtnClicked, reportType }: Props) => {
    const { t } = useTranslation();
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const newReport = useCreateSingleReportCSV(reportType, extendedFormValues);
    const exportCsvAction = () => {
        setSaveBtnClicked(true);
        newReport.remove();
        newReport.refetch();
    };

    if (newReport.isError) setErrorModalOpen(true);

    if (newReport.isSuccess) {
        switch (reportType) {
            case 'merchantsummary': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'New Merchant Summary Report - Export CSV',
                        text: 'Export New Merchant Summary Report'
                    }
                });
                break;
            }
            case 'performance': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'New Rule Performance Report - Export CSV',
                        text: 'Export New Rule Performance Report'
                    }
                });
                break;
            }
            default: {
                break;
            }
        }
    }

    return (
        <>
            {newReport.isInitialLoading ? (
                <Loading size='sm' fillName='orange' />
            ) : (
                <Button
                    variant='secondary'
                    size='small'
                    data-testid='upload-btn'
                    onClick={handleOnClickEvent(exportCsvAction)}
                    onKeyDown={handleKeyEvent(exportCsvAction)}
                    disabled={!extendedFormValues}
                >
                    {t('Export CSV')}
                </Button>
            )}
            <ExportCSVErrorModal
                isOpen={errorModalOpen}
                closeModal={() => {
                    newReport.refetch();
                    setErrorModalOpen(false);
                }}
                createReport={() => newReport.refetch()}
            />
        </>
    );
};

export default ExportCSV;
